import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { endOfMonth, format, isSameMonth, startOfMonth } from "date-fns";
import { merge } from "lodash-es";
import type React from "react";
import { useState } from "react";
import { twJoin } from "tailwind-merge";

import type { ConsumptionResultDto, ServicePartnerPageDetailsDto } from "@/api/types";
import { TrendBadge } from "@/components/Badge/TrendBadge";
import { DateRange } from "@/components/DateRange/DateRange";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { WatchSquareIcon } from "@/components/Icons/WatchSquareIcon";
import { PeriodPicker } from "@/components/PeriodPicker/PeriodPicker";
import { Button } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { Notice } from "@/design-system/Notice";
import { Spinner } from "@/design-system/Spinner";
import { Stack } from "@/design-system/Stack";
import { SUPPORT_EMAIL } from "@/helpers/constants";
import { asUtc } from "@/helpers/date";
import { useBool } from "@/hooks/useBool";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { useServicePartnersQueries } from "@/queries/service-partners/queryOptions";
import { Trans, useTranslation } from "@/translations";

import electricityIcon from "../../assets/iqbi-electricity.png";
import heatingIcon from "../../assets/iqbi-heating.png";
import solarIcon from "../../assets/iqbi-solar.png";
import waterIcon from "../../assets/iqbi-water.png";
import { ServiceDetailsBlockCard } from "../ServiceDetailsBlockCard";
import { IqbiDetailsConsentModal } from "./IqbiDetailsConsentModal";
import { IqbiDetailsVerifyAddressModal } from "./IqbiDetailsVerifyAddressModal";

interface IqbiDetailsConsumptionCardProps {
  serviceDetails: ServicePartnerPageDetailsDto;
}

const DEFAULT_IQBI_CONSUMPTION: ConsumptionResultDto[] = [
  {
    requestedMonth: {
      value: 0,
    },
    unitType: "electricity",
    percentageChange: 0,
    previousMonth: {
      value: 0,
    },
    usageChange: 0,
  },
  {
    requestedMonth: {
      value: 0,
    },
    unitType: "gas",
    percentageChange: 0,
    previousMonth: {
      value: 0,
    },
    usageChange: 0,
  },
  {
    requestedMonth: {
      value: 0,
    },
    unitType: "water",
    percentageChange: 0,
    previousMonth: {
      value: 0,
    },
    usageChange: 0,
  },
];

export function IqbiDetailsConsumptionCard({ serviceDetails }: IqbiDetailsConsumptionCardProps): React.ReactNode {
  const clockDate = useCurrentDate();
  const [isVerifyAddressModalOpen, verifyAddressModalHandlers] = useBool(false);
  const [isConsentModalOpen, consentModalHandlers] = useBool(false);
  const [queryDate, setQueryDate] = useState<Date>(() => startOfMonth(new Date()));
  const { t } = useTranslation();
  const servicePartnersQueries = useServicePartnersQueries();

  const iqbiStatus = serviceDetails.partnerProperties.iqbi?.status;
  const month = format(asUtc(queryDate), "yyyy-MM-dd");
  const {
    data: iqbiConsumption,
    isFetching: isFetchingIqbiConsumption,
    error: iqbiConsumptionError,
  } = useQuery({
    ...servicePartnersQueries.iqbiConsumption(serviceDetails.id, month),
    placeholderData: keepPreviousData,
    enabled: iqbiStatus === "ready",
  });

  return (
    <>
      <ServiceDetailsBlockCard title={t("page.service-details.iqbi-consumption.title")}>
        <section className="relative size-full">
          {iqbiStatus === "noConsent" && (
            <>
              <IqbiDetailsConsumptionCardOverview consumptions={DEFAULT_IQBI_CONSUMPTION} date={queryDate} />
              <aside className="absolute inset-0 flex size-full items-center justify-center bg-background-default/60 backdrop-blur-xs">
                <Button onClick={consentModalHandlers.setTrue}>
                  {t("page.service-details.iqbi.give-consent-btn")}
                </Button>
              </aside>
            </>
          )}

          {iqbiStatus === "notVerified" && (
            <>
              <IqbiDetailsConsumptionCardOverview consumptions={DEFAULT_IQBI_CONSUMPTION} date={queryDate} />
              <aside className="absolute inset-0 flex size-full items-center justify-center bg-background-default/60 backdrop-blur-xs">
                <Button onClick={verifyAddressModalHandlers.setTrue}>
                  {t("page.service-details.iqbi.verify-btn")}
                </Button>
              </aside>
            </>
          )}

          {(iqbiStatus === "waitingForConsentApproval" || iqbiStatus === "contactSupport") && (
            <>
              <IqbiDetailsConsumptionCardOverview consumptions={DEFAULT_IQBI_CONSUMPTION} date={queryDate} />
              <aside className="absolute inset-0 flex size-full items-center justify-center bg-background-default/60 backdrop-blur-xs">
                <section className="w-full max-w-measure-compact px-space-sm @md:px-0">
                  <Notice
                    variant="special"
                    icon={<WatchSquareIcon />}
                    customBody={
                      <p className="text-caption-emphasis">
                        <Trans
                          i18nKey={
                            iqbiStatus === "waitingForConsentApproval"
                              ? "page.service-details.iqbi.waiting-for-consent-approval.description"
                              : "page.service-details.iqbi.contact-support.description"
                          }
                          components={{
                            email: (
                              <a className="text-text-brand" href={`mailto:${SUPPORT_EMAIL}`}>
                                {SUPPORT_EMAIL}
                              </a>
                            ),
                          }}
                        />
                      </p>
                    }
                  />
                </section>
              </aside>
            </>
          )}

          {iqbiStatus === "ready" && (
            <>
              <PeriodPicker type="month" onChange={setQueryDate} value={queryDate} max={clockDate} />
              <section className="relative size-full">
                {/* Placeholder for calculating desired height to reduce CLS in non-success states */}
                {(isFetchingIqbiConsumption || !iqbiConsumption?.data || iqbiConsumptionError) && (
                  <>
                    <span className="absolute top-0 left-0 -z-10 size-full bg-background-default" />
                    <div className="invisible opacity-0" aria-hidden>
                      <IqbiDetailsConsumptionCardOverview consumptions={DEFAULT_IQBI_CONSUMPTION} date={queryDate} />
                    </div>
                  </>
                )}

                {/* Loading state */}
                {isFetchingIqbiConsumption && (
                  <aside className="absolute inset-0 z-raised flex items-center justify-center">
                    <Spinner size="xl" />
                  </aside>
                )}

                {/* Empty/Error state */}
                {((!isFetchingIqbiConsumption && !iqbiConsumption?.data) || iqbiConsumptionError) && (
                  <p className="absolute top-1/2 left-1/2 -translate-1/2 text-text-secondary">
                    {t("page.service-details.iqbi-consumption.no-data")}
                  </p>
                )}
              </section>
              {/* Success state */}
              {!isFetchingIqbiConsumption && iqbiConsumption?.data && (
                <IqbiDetailsConsumptionCardOverview
                  // Fill unavailable energy types with default values to prevent CLS
                  consumptions={merge(DEFAULT_IQBI_CONSUMPTION, iqbiConsumption.data)}
                  date={queryDate}
                  hideZeroValues
                />
              )}
            </>
          )}
        </section>
      </ServiceDetailsBlockCard>
      {/* Modals */}
      <IqbiDetailsVerifyAddressModal
        isOpen={isVerifyAddressModalOpen}
        onClose={verifyAddressModalHandlers.setFalse}
        serviceId={serviceDetails.id}
      />
      <IqbiDetailsConsentModal
        isOpen={isConsentModalOpen}
        onClose={consentModalHandlers.setFalse}
        serviceId={serviceDetails.id}
      />
    </>
  );
}

interface IqbiDetailsConsumptionCardOverviewProps {
  consumptions: ConsumptionResultDto[];
  date: Date;
  hideZeroValues?: boolean;
}

function IqbiDetailsConsumptionCardOverview({
  consumptions,
  date,
  hideZeroValues,
}: IqbiDetailsConsumptionCardOverviewProps): React.ReactNode {
  const clockDate = useCurrentDate();
  const { t, i18n } = useTranslation();

  const getConsumptionIcon = (unitType: ConsumptionResultDto["unitType"]) => {
    switch (unitType) {
      case "electricity":
        return electricityIcon;
      case "gas":
        return heatingIcon;
      case "water":
        return waterIcon;
      default:
        return solarIcon;
    }
  };

  const getConsumptionUnit = (unitType: ConsumptionResultDto["unitType"]) => {
    switch (unitType) {
      case "electricity":
        return "kWh";
      case "gas":
      case "water":
      default:
        return "m3";
    }
  };

  return (
    <Stack align="start">
      <section className="flex w-full flex-col gap-space-xl gap-x-20 py-space-lg @md:grid @md:grid-cols-2">
        {consumptions.map((consumption) => {
          const icon = getConsumptionIcon(consumption.unitType);
          const unit = getConsumptionUnit(consumption.unitType);
          const isHidden =
            hideZeroValues && consumption.requestedMonth.value === 0 && consumption.percentageChange === 0;

          return (
            <article
              className={twJoin("flex items-center justify-between gap-space-sm", isHidden && "invisible opacity-0")}
              key={consumption.unitType}
              aria-hidden={isHidden}
            >
              <Inline gap="md" align="center">
                <img src={icon} width={40} height={40} alt="" />
                <p className="flex items-baseline gap-space-xs">
                  <span className="text-heading-1">{consumption.requestedMonth.value}</span>
                  <span>{unit}</span>
                </p>
              </Inline>

              <TrendBadge
                method="percentage"
                referenceValue={0}
                value={consumption.percentageChange || 0}
                isTrendInversed
                shouldRoundUp
              />
            </article>
          );
        })}
      </section>
      <p className="text-text-secondary">
        {isSameMonth(date, clockDate) ? (
          <span>* {t("page.service-details.date.until", { date: formatDate(i18n, "date", clockDate) })}</span>
        ) : (
          <span>
            * <DateRange start={startOfMonth(date)} end={endOfMonth(date)} format="noTime" inline />
          </span>
        )}
      </p>
    </Stack>
  );
}
